import { useEffect, useRef, useState } from 'react';

/**
 * CheckFactorGraph - Displays boat speed over 3 strokes to analyze check factor
 * 
 * Check Factor shows how smoothly the boat runs between strokes.
 * A good check factor means minimal speed drop during recovery phase.
 * 
 * X-axis: Time/stroke phase
 * Y-axis: Boat speed (m/s)
 */
export default function CheckFactorGraph({ 
  strokeData = [], // Array of speed readings: [{ timestamp, speed, strokePhase }]
  currentSpeed = 0,
  strokeRate = 0,
  className = ''
}) {
  const canvasRef = useRef(null);
  const [checkFactor, setCheckFactor] = useState(0);
  const [avgPeakSpeed, setAvgPeakSpeed] = useState(0);
  const [avgMinSpeed, setAvgMinSpeed] = useState(0);
  
  // Calculate check factor from stroke data
  useEffect(() => {
    if (strokeData.length < 10) return;
    
    // Find peaks (drive phase max) and valleys (recovery phase min) in last 3 strokes
    const recentData = strokeData.slice(-60); // ~3 strokes worth of data at 20Hz
    
    let peaks = [];
    let valleys = [];
    
    for (let i = 1; i < recentData.length - 1; i++) {
      const prev = recentData[i - 1]?.speed || 0;
      const curr = recentData[i]?.speed || 0;
      const next = recentData[i + 1]?.speed || 0;
      
      if (curr > prev && curr > next && curr > 0.5) {
        peaks.push(curr);
      }
      if (curr < prev && curr < next && curr > 0) {
        valleys.push(curr);
      }
    }
    
    if (peaks.length > 0 && valleys.length > 0) {
      const avgPeak = peaks.reduce((a, b) => a + b, 0) / peaks.length;
      const avgValley = valleys.reduce((a, b) => a + b, 0) / valleys.length;
      
      setAvgPeakSpeed(avgPeak);
      setAvgMinSpeed(avgValley);
      
      // Check factor = percentage of speed retained (higher is better)
      // CF = (min speed / max speed) * 100
      const cf = avgPeak > 0 ? (avgValley / avgPeak) * 100 : 0;
      setCheckFactor(Math.round(cf));
    }
  }, [strokeData]);
  
  // Draw the graph
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear canvas
    ctx.fillStyle = '#18181B';
    ctx.fillRect(0, 0, width, height);
    
    // If no data, show placeholder
    if (strokeData.length < 5) {
      ctx.fillStyle = '#71717A';
      ctx.font = 'bold 14px JetBrains Mono, monospace';
      ctx.textAlign = 'center';
      ctx.fillText('Waiting for GPS data...', width / 2, height / 2);
      return;
    }
    
    // Get last ~3 strokes of data (assuming ~20 readings per stroke at 20Hz for 1 stroke/3 sec)
    const displayData = strokeData.slice(-60);
    
    if (displayData.length < 2) return;
    
    // Find min/max for scaling
    const speeds = displayData.map(d => d.speed || 0).filter(s => s > 0);
    const maxSpeed = Math.max(...speeds, 0.1) * 1.1;
    const minSpeed = Math.min(...speeds, 0) * 0.9;
    const speedRange = maxSpeed - minSpeed || 1;
    
    // Draw grid lines
    ctx.strokeStyle = '#3F3F46';
    ctx.lineWidth = 1;
    
    // Horizontal grid lines (speed levels)
    for (let i = 0; i <= 4; i++) {
      const y = 30 + (i * (height - 60) / 4);
      ctx.beginPath();
      ctx.moveTo(50, y);
      ctx.lineTo(width - 20, y);
      ctx.stroke();
      
      // Speed labels
      const speedLabel = (maxSpeed - (i * speedRange / 4)).toFixed(1);
      ctx.fillStyle = '#71717A';
      ctx.font = '10px JetBrains Mono, monospace';
      ctx.textAlign = 'right';
      ctx.fillText(speedLabel, 45, y + 3);
    }
    
    // Draw Y-axis label
    ctx.save();
    ctx.translate(12, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = '#A1A1AA';
    ctx.font = 'bold 10px JetBrains Mono, monospace';
    ctx.textAlign = 'center';
    ctx.fillText('SPEED (M/S)', 0, 0);
    ctx.restore();
    
    // Draw X-axis label
    ctx.fillStyle = '#A1A1AA';
    ctx.font = 'bold 10px JetBrains Mono, monospace';
    ctx.textAlign = 'center';
    ctx.fillText('← 3 STROKES →', width / 2, height - 5);
    
    // Draw speed line
    ctx.strokeStyle = '#22C55E';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    
    const graphWidth = width - 70;
    const graphHeight = height - 60;
    const graphLeft = 50;
    const graphTop = 30;
    
    displayData.forEach((point, index) => {
      const x = graphLeft + (index / (displayData.length - 1)) * graphWidth;
      const normalizedSpeed = (point.speed - minSpeed) / speedRange;
      const y = graphTop + graphHeight - (normalizedSpeed * graphHeight);
      
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();
    
    // Draw fill under the line
    ctx.lineTo(graphLeft + graphWidth, graphTop + graphHeight);
    ctx.lineTo(graphLeft, graphTop + graphHeight);
    ctx.closePath();
    const gradient = ctx.createLinearGradient(0, graphTop, 0, graphTop + graphHeight);
    gradient.addColorStop(0, 'rgba(34, 197, 94, 0.3)');
    gradient.addColorStop(1, 'rgba(34, 197, 94, 0.05)');
    ctx.fillStyle = gradient;
    ctx.fill();
    
    // Draw current speed marker
    if (displayData.length > 0) {
      const lastPoint = displayData[displayData.length - 1];
      const lastX = graphLeft + graphWidth;
      const lastY = graphTop + graphHeight - ((lastPoint.speed - minSpeed) / speedRange * graphHeight);
      
      ctx.fillStyle = '#22C55E';
      ctx.beginPath();
      ctx.arc(lastX, lastY, 6, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(lastX, lastY, 3, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Draw peak and valley indicators
    if (avgPeakSpeed > 0 && avgMinSpeed > 0) {
      const peakY = graphTop + graphHeight - ((avgPeakSpeed - minSpeed) / speedRange * graphHeight);
      const valleyY = graphTop + graphHeight - ((avgMinSpeed - minSpeed) / speedRange * graphHeight);
      
      // Peak line (dashed green)
      ctx.strokeStyle = '#4ADE80';
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(graphLeft, peakY);
      ctx.lineTo(graphLeft + graphWidth, peakY);
      ctx.stroke();
      
      // Valley line (dashed orange)
      ctx.strokeStyle = '#FB923C';
      ctx.beginPath();
      ctx.moveTo(graphLeft, valleyY);
      ctx.lineTo(graphLeft + graphWidth, valleyY);
      ctx.stroke();
      
      ctx.setLineDash([]);
    }
    
  }, [strokeData, avgPeakSpeed, avgMinSpeed]);
  
  // Determine check factor quality
  const getCheckQuality = (cf) => {
    if (cf >= 85) return { label: 'EXCELLENT', color: '#22C55E' };
    if (cf >= 75) return { label: 'GOOD', color: '#84CC16' };
    if (cf >= 65) return { label: 'FAIR', color: '#EAB308' };
    if (cf >= 50) return { label: 'NEEDS WORK', color: '#F97316' };
    return { label: 'POOR', color: '#EF4444' };
  };
  
  const quality = getCheckQuality(checkFactor);

  return (
    <div className={`bg-zinc-900 rounded-xl p-3 ${className}`}>
      {/* Header with Check Factor value */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-bold text-zinc-500 uppercase tracking-wider">CHECK FACTOR</div>
          <div className="flex items-baseline gap-2">
            <span 
              className="text-4xl font-bold" 
              style={{ fontFamily: 'JetBrains Mono, monospace', color: quality.color }}
            >
              {checkFactor}%
            </span>
            <span 
              className="text-xs font-bold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: quality.color + '20', color: quality.color }}
            >
              {quality.label}
            </span>
          </div>
        </div>
        
        {/* Mini stats */}
        <div className="text-right">
          <div className="text-xs text-zinc-500">
            <span className="text-green-400">↑ {avgPeakSpeed.toFixed(2)}</span>
            <span className="mx-1">/</span>
            <span className="text-orange-400">↓ {avgMinSpeed.toFixed(2)}</span>
            <span className="text-zinc-600 ml-1">m/s</span>
          </div>
          <div className="text-xs text-zinc-600">
            {strokeRate} SPM
          </div>
        </div>
      </div>
      
      {/* Graph Canvas */}
      <canvas 
        ref={canvasRef}
        width={340}
        height={160}
        className="w-full rounded-lg"
        style={{ maxWidth: '100%', height: 'auto' }}
      />
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-zinc-500">
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-green-400 inline-block" style={{ borderStyle: 'dashed' }}></span>
          Peak
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-orange-400 inline-block" style={{ borderStyle: 'dashed' }}></span>
          Valley
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-0.5 bg-green-500 inline-block"></span>
          Speed
        </span>
      </div>
    </div>
  );
}
